import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap";
import { Container, Draggable } from "react-smooth-dnd";
import { arrayMoveImmutable } from "array-move";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import SweetAlert from "sweetalert2";
import { toast } from "react-toastify";
import PreLoader from "../../../../share-components/ui/preLoader";
import WearItToFormModal from "./wearItToFormModal";
import {
  getProductWearItTosApi,
  addProductWearItToApi,
  updateProductWearItToApi,
  deleteProductWearItToApi,
  reorderProductWearItTosApi,
} from "../../../../services/manage-product/productService";

const sortByPosition = (rows) =>
  [...rows].sort((a, b) => (a.display_order - b.display_order) || (a.id - b.id));

// The shared axios interceptor (src/utils/api.jsx) already shows a toast itself for 422
// and 401 responses (and rejects with the raw response body, not an Error, in those two
// cases) — only re-toast here when the rejection is a real AxiosError that wasn't already
// surfaced globally, so mutation failures aren't double-toasted. Same convention as
// manageSignatureDetailsModal.jsx / manageHowToStyleVideoModal.jsx.
const notifyMutationError = (err, fallback) => {
  if (err && err.isAxiosError && err.response?.status !== 404) {
    toast.error(err.response?.data?.message || err.message || fallback);
  }
};

const buildWearItToFormData = (values) => {
  const data = new FormData();
  data.append("title", values.title);
  // Both icon and media_path are omitted entirely (not even as an empty field) when no
  // new file was picked — ProductWearItToController::update only swaps icon/media_path
  // when hasFile(...) is true, so an absent field is what preserves the existing asset.
  if (values.iconFile) data.append("icon", values.iconFile);
  if (values.mediaFile) data.append("media_path", values.mediaFile);
  data.append("display_order", values.display_order ?? 0);
  return data;
};

/**
 * List-managing modal for a product's "Wear it to" entries — fetch, add, edit, delete.
 * Supports any number of coexisting entries (a real array, not a single-record view).
 * The actual create/edit form is WearItToFormModal (Phase 5); this component supplies
 * its `onSubmit` with the real Phase 4 API calls and owns the list state around it.
 */
const ManageWearItToModal = ({ open, onClose, productId, productCode, productColour }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [mutating, setMutating] = useState(false);
  const [reordering, setReordering] = useState(false);

  // A reorder persist and any other mutation must never overlap — both would race to write
  // display_order for the same scoped product, so one combined flag gates both drag and
  // every other Add/Edit/Delete control. Mirrors manageSignatureDetailsModal.jsx's `busy`.
  const busy = mutating || reordering;

  // null | "create" | "edit"
  const [formMode, setFormMode] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);

  const fetchEntries = (silent = false) => {
    if (!productId) return;
    if (!silent) {
      setLoading(true);
      setLoadError("");
    }
    getProductWearItTosApi(productId)
      .then(({ response }) => {
        setEntries(sortByPosition(Array.isArray(response) ? response : []));
      })
      .catch((err) => {
        // A silent (post-mutation) reconcile failing shouldn't blow away the optimistic
        // state the user already sees — only surface the retry UI for the initial load.
        if (!silent) {
          setLoadError(err?.response?.data?.message || err?.message || "Failed to load Wear It To entries.");
        }
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  useEffect(() => {
    // Fully clear stale entries/product scope so a later reopen never flashes a previous
    // product's rows, whether we're closing or switching to a different productId.
    setEntries([]);
    setLoadError("");
    setLoading(false);
    setFormMode(null);
    setEditingRecord(null);

    if (open && productId) {
      fetchEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, productId]);

  const openAddForm = () => {
    setEditingRecord(null);
    setFormMode("create");
  };

  const openEditForm = (row) => {
    setEditingRecord(row);
    setFormMode("edit");
  };

  const closeForm = () => {
    setFormMode(null);
    setEditingRecord(null);
  };

  // Passed to WearItToFormModal as `onSubmit`. Must reject (not swallow) on failure so
  // the form modal itself stays open and renders the field-level errors — only a
  // resolved promise here closes the form and refreshes the list.
  const handleFormSubmit = (values) => {
    if (formMode === "edit" && editingRecord) {
      // Position is left untouched on a plain edit — only dragging (handleReorderDrop)
      // changes display_order, matching manageSignatureDetailsModal.jsx.
      const data = buildWearItToFormData({ ...values, display_order: editingRecord.display_order });
      return updateProductWearItToApi(editingRecord.id, productId, data).then(({ response }) => {
        setEntries((prev) => sortByPosition(prev.map((e) => (e.id === editingRecord.id ? response : e))));
        toast.success("Wear it to entry updated.");
        closeForm();
        // Server rows are the real source of truth (survives close/reopen on their own
        // merit) — this just keeps the in-memory list in sync right away too.
        fetchEntries(true);
      });
    }

    const nextPosition = entries.length ? Math.max(...entries.map((e) => e.display_order)) + 1 : 0;
    const data = buildWearItToFormData({ ...values, display_order: nextPosition });
    return addProductWearItToApi(productId, data).then(({ response }) => {
      setEntries((prev) => sortByPosition([...prev, response]));
      toast.success("Wear it to entry added.");
      closeForm();
      fetchEntries(true);
    });
  };

  const handleDelete = (row) => {
    SweetAlert.fire({
      title: "Delete Wear It To entry?",
      html: `This will permanently remove <b>${row.title}</b>.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then((result) => {
      if (!result.isConfirmed) return;

      setMutating(true);
      deleteProductWearItToApi(row.id)
        .then(() => {
          setEntries((prev) => prev.filter((e) => e.id !== row.id));
          toast.success("Wear it to entry deleted.");
        })
        .catch((err) => {
          if (err?.isAxiosError && err.response?.status === 404) {
            // Already removed (e.g. by another editor) — drop it locally, no error toast.
            setEntries((prev) => prev.filter((e) => e.id !== row.id));
            return;
          }
          notifyMutationError(err, "Failed to delete the entry.");
        })
        .finally(() => setMutating(false));
    });
  };

  const handleReorderDrop = ({ removedIndex, addedIndex }) => {
    // Guard even though the handle is visually/pointer-disabled while busy — belt and
    // braces against any stray drop event, and against no-op drops.
    if (busy || removedIndex === null || addedIndex === null || removedIndex === addedIndex) return;

    const lastKnownGoodOrder = entries;
    const reordered = arrayMoveImmutable(entries, removedIndex, addedIndex).map((row, index) => ({
      ...row,
      display_order: index,
    }));

    setEntries(reordered);
    setReordering(true);

    const items = reordered.map((row) => ({ id: row.id, display_order: row.display_order }));

    reorderProductWearItTosApi(productId, items)
      .then(({ response }) => {
        // The server's persisted rows are the new source of truth (survives close/reopen
        // and reload on their own merit — this just keeps the in-memory view in sync now).
        setEntries(sortByPosition(Array.isArray(response) ? response : reordered));
      })
      .catch((err) => {
        setEntries(lastKnownGoodOrder);
        notifyMutationError(err, "Failed to save the new order.");
      })
      .finally(() => setReordering(false));
  };

  return (
    <>
      <Modal isOpen={open} toggle={onClose} className="modal-dialog-centered" size="lg">
        <ModalHeader toggle={onClose} tag="div">
          <div>Manage Wear It To</div>
          {(productCode || (Array.isArray(productColour) && productColour.filter(Boolean).length > 0)) && (
            <div style={{ fontSize: "0.8rem", fontWeight: 400, color: "#888", marginTop: 2 }}>
              {[productCode, Array.isArray(productColour) ? productColour.filter(Boolean).join(", ") : productColour]
                .filter(Boolean)
                .join(" · ")}
            </div>
          )}
        </ModalHeader>
        <ModalBody style={{ minHeight: 200, position: "relative" }}>
          {loading && <PreLoader withoutText />}

          {!loading && loadError && (
            <div className="alert alert-danger">
              <div className="font-weight-bold mb-1">Wear It To entries could not be loaded</div>
              <div className="mb-2">{loadError}</div>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => fetchEntries()}>
                Retry
              </button>
            </div>
          )}

          {!loading && !loadError && (
            <>
              {entries.length > 0 ? (
                <div style={{ maxHeight: 360, overflowY: "auto", marginBottom: 16 }}>
                  <Container dragHandleSelector=".drag-handle" lockAxis="y" onDrop={handleReorderDrop}>
                    {entries.map((row) => (
                      <Draggable key={row.id}>
                        <div className="d-flex align-items-center border-bottom py-2" style={{ background: "#fff" }}>
                          <span
                            className="drag-handle"
                            style={{
                              cursor: busy ? "not-allowed" : "grab",
                              pointerEvents: busy ? "none" : "auto",
                              opacity: busy ? 0.4 : 1,
                              marginRight: 8,
                              flexShrink: 0,
                            }}
                          >
                            <DragHandleIcon />
                          </span>
                          {Number(row.media_type) === 2 ? (
                            <video
                              muted
                              src={row.media_path}
                              style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4, marginRight: 12, flexShrink: 0, background: "#000" }}
                            />
                          ) : (
                            <img
                              src={row.media_path}
                              alt=""
                              style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 4, marginRight: 12, flexShrink: 0 }}
                            />
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontWeight: 600,
                                wordBreak: "break-word",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {row.icon && (
                                <img
                                  src={row.icon}
                                  alt=""
                                  style={{ width: 18, height: 18, objectFit: "contain", marginRight: 6, verticalAlign: "middle" }}
                                />
                              )}
                              {row.title}
                            </div>
                            <small className="text-muted text-capitalize">{Number(row.media_type) === 2 ? "video" : "image"}</small>
                          </div>
                          <div className="d-flex align-items-center" style={{ gap: 12, marginLeft: 12, flexShrink: 0 }}>
                            <Button color="link" className="p-0" disabled={busy} onClick={() => openEditForm(row)}>
                              <i className="icon-pencil" />
                            </Button>
                            <Button color="link" className="p-0 text-danger" disabled={busy} onClick={() => handleDelete(row)}>
                              <i className="icon-trash" />
                            </Button>
                          </div>
                        </div>
                      </Draggable>
                    ))}
                  </Container>
                </div>
              ) : (
                <div className="text-muted mb-3">No Wear It To entries yet.</div>
              )}

              <Button color="primary" disabled={busy} onClick={openAddForm}>
                + Add Wear It To
              </Button>
            </>
          )}
        </ModalBody>
      </Modal>

      <WearItToFormModal
        open={Boolean(formMode)}
        onClose={closeForm}
        mode={formMode || "create"}
        record={editingRecord}
        onSubmit={handleFormSubmit}
      />
    </>
  );
};

export default ManageWearItToModal;
